import * as THREE from 'three';
import { Entity, type EntityCtx } from '../Entity';
import { Shell } from './Shell';
import { Explosion } from './Explosion';
import { spawnWreckage, WRECK_PALETTES } from './Wreckage';
import { useStore } from '../../state/store';
import { endRunOnLastUnit, aimPointFor } from '../possession';
import { interceptPoint, entityVelocity } from '../aim';

/**
 * Friendly armour — a Renault FT-17 light tank. Roams the field hunting enemy ground units
 * (tanks + ground bosses), closing to cannon range and shelling them. The player can jump
 * in to aim the turret manually; it auto-fires when a ground target crosses the sights.
 * Distinct FT silhouette (rear tail skid, front track horns, domed rotating turret) +
 * Allied blue-grey livery so it never reads like the field-grey enemy rhomboids.
 */
/** Cannon barrel centre-to-tip, used to spawn shells at the muzzle rather than mid-barrel. */
const BARREL_HALF = 2.0;
const GROUND_Y = -468;
/** Brawler durability — the tank's role is the line that HOLDS (planes out-damage it 6:1, so
 *  survivability is its identity). Enemy shells hit for 3 → ~7 hits to kill, vs 4 before. */
const HP = 22;
const SPEED = 16;
const FIRE_RANGE = 460;
const HUNT_RANGE = 3000;          // how far an idle tank will set out to hunt enemy armour
const FIRE_INTERVAL = 0.8;
const FIRE_CONE = THREE.MathUtils.degToRad(7);
const SHELL_SPEED = 600;
const TURRET_SLEW = 1.4;          // rad/s AI turret tracking
const PLAYER_TURRET_RATE = 2.0;
const DRIVE_STEER_RATE = 1.3;     // rad/s hull steering when the player drives

export class AlliedTank extends Entity {
  camera: THREE.PerspectiveCamera | null = null;
  readonly speed = SPEED;
  forward = new THREE.Vector3(0, 0, -1);
  private turret: THREE.Object3D;
  private cannon: THREE.Object3D;
  private turretYaw = 0;
  private hullYaw = 0;
  private _interior?: THREE.Group; // commander-hatch framing (cupola + sightline gun), crewed only
  private fireCooldown = 0.6;
  private dying = false;
  private deathTimer = 0;
  private _muzzle = new THREE.Vector3();
  private _fwd = new THREE.Vector3();
  private _toLocal = new THREE.Vector3();
  private _invMat = new THREE.Matrix4();

  constructor(pos: THREE.Vector3, label = 'TANK') {
    super('ally');
    this.friendly = true;
    this.controllable = true;
    this.unitLabel = label;
    this.hp = HP;
    this.radius = 9;
    this.group.position.copy(pos);
    this.group.position.y = GROUND_Y;
    const mesh = makeAlliedTankMesh();
    this.turret = mesh.userData.turret as THREE.Object3D;
    this.cannon = mesh.userData.cannon as THREE.Object3D;
    this.group.add(mesh);

    // FT-17 is a compact light tank — tighter OBB than the heavy rhomboid enemy tanks.
    this.localBounds = new THREE.Box3(
      new THREE.Vector3(-2.4, -0.5, -5.4),
      new THREE.Vector3(2.4, 4.4, 4.6),
    );
  }

  alive(): boolean { return !this.dying; }
  hpFrac(): number { return THREE.MathUtils.clamp(this.hp / HP, 0, 1); }

  possess(camera: THREE.PerspectiveCamera): void {
    this.controlled = true;
    this.camera = camera;
    // Commander "unbuttoned": head and shoulders out of the open top hatch — eye just above the
    // cupola, half your body above the turret and half inside it. You see the open hatch rim around
    // you and the 37 mm gun running forward to the aim point. (The real hull gun sits low under the
    // tall domed turret and is hidden from up here, so the crewed view carries its own gun at the
    // commander's sightline — see makeTankInterior.) Mounted on the turret so the view swings with
    // the gun as you traverse.
    this.turret.add(camera);
    camera.position.set(0, 2.45, 0.1);
    camera.rotation.set(-0.1, 0, 0);
    camera.fov = 64;
    camera.updateProjectionMatrix();
    if (!this._interior) { this._interior = makeTankInterior(); this.turret.add(this._interior); }
    this._interior.visible = true;
    // Sync the driving heading to the hull's current orientation so control is seamless.
    this.hullYaw = this.group.rotation.y;
    useStore.getState().setHp(Math.max(0, Math.ceil(this.hp)), HP);
  }

  release(): void {
    this.controlled = false;
    useStore.getState().setTankTargeting(null); // drop the targeting overlay when you leave the tank
    if (this._interior) this._interior.visible = false; // hide crewed framing in the external model
    if (this.camera && this.camera.parent === this.turret) this.turret.remove(this.camera);
    this.camera = null;
  }

  damage(amount: number, ctx: EntityCtx): void {
    if (this.dying) return;
    this.hp -= amount;
    if (this.controlled) {
      useStore.getState().takeDamage();
      useStore.getState().setHp(Math.max(0, Math.floor(this.hp)), HP);
    }
    if (this.hp <= 0) this.onDestroyed(ctx);
  }

  private onDestroyed(ctx: EntityCtx) {
    if (this.controlled) {
      const handed = ctx.game.onControlledLost(this);
      if (handed) this.release();
      else endRunOnLastUnit(this, ctx);
    }
    this.dying = true;
    spawnWreckage(ctx, this.group.position.clone(), 'tank', WRECK_PALETTES.alliedTank,
      { baseVel: this.forward.clone().multiplyScalar(SPEED), count: 8, boom: 1.0 });
    this.dead = true;
  }

  update(ctx: EntityCtx): void {
    const { game, dt } = ctx;
    if (this.dying) {
      this.deathTimer += dt;
      if (this.deathTimer < 0.6 && Math.random() < 0.5) {
        const off = new THREE.Vector3((Math.random() - 0.5) * 6, 2, (Math.random() - 0.5) * 6);
        new Explosion(this.group.position.clone().add(off), 13, 0.5, 'fire').addTo(ctx);
      }
      // Keep the wreck if the game-over camera is parked on it; only sweep during play.
      if (this.deathTimer > 5 && useStore.getState().phase === 'playing') this.dead = true;
      return;
    }
    if (useStore.getState().phase === 'gameover') return;

    const target = this.acquireTarget(game);
    this.engageTargetId = target ? target.id : 0; // for the map's "engaging" line
    let aimWorld: THREE.Vector3 | null = null;
    if (target) {
      const muzzle = this.muzzleWorld().clone();
      const base = aimPointFor(target, muzzle, new THREE.Vector3());
      aimWorld = interceptPoint(muzzle, base, entityVelocity(target, 30), SHELL_SPEED);
    }
    const stank = useStore.getState();
    const aiDriven = !this.controlled || stank.ai || stank.mapOpen;

    if (aiDriven) {
      // Where to drive comes from the standing order; what to shoot is whatever ground
      // enemy is in range (handled via `target`/`aimWorld`), independent of the order.
      let driveTo: THREE.Vector3 | null = null;
      let stopDist = FIRE_RANGE * 0.85;
      const ord = this.order;
      if (ord.type === 'attack') {
        const t = game.enemyById(ord.targetId);
        if (t) driveTo = t.position; else this.order = { type: 'patrol' };
      } else if (ord.type === 'defend') {
        const v = game.villageCenter(ord.villageIndex);
        if (v) { driveTo = v; stopDist = 40; } else this.order = { type: 'patrol' };
      } else if (ord.type === 'escort') {
        const u = game.friendlyById(ord.unitId);
        if (u && u !== this) { driveTo = u.position; stopDist = 60; } else this.order = { type: 'patrol' };
      }
      if (!driveTo) {
        // Default posture (patrol): proactively hunt the nearest enemy armour anywhere on the
        // field; if none is about, fall back to holding the nearest village so tanks DEFEND
        // rather than sit idle. (Shooting is still gated separately on `target`/range below.)
        const hunt = this.nearestEnemyArmor(game, HUNT_RANGE);
        if (hunt) {
          driveTo = hunt.position;
        } else {
          const v = this.nearestVillage(game);
          if (v) { driveTo = v; stopDist = 45; }
        }
      }
      if (driveTo) {
        const to = driveTo.clone().sub(this.group.position); to.y = 0;
        const dist = to.length();
        if (dist > 1e-3) to.multiplyScalar(1 / dist);
        this.forward.lerp(to, 0.05).normalize();
        this.group.rotation.set(0, Math.atan2(this.forward.x, this.forward.z), 0);
        if (dist > stopDist) this.group.position.addScaledVector(this.forward, SPEED * dt);
      }
      if (aimWorld) this.aimTurretToward(aimWorld, TURRET_SLEW * dt);
    } else {
      // LEFT stick drives the hull: push forward/back to move, left/right to steer.
      // Positive world-Y rotation turns the nose left, so steer-right (drive.x > 0)
      // subtracts. Steering only bites while moving, and reverses when backing up — so it
      // feels like a vehicle, not a turntable spinning in place.
      const drive = game.input.getLeftStick();
      // Keyboard W = forward: the key-stick uses the plane's arcade convention (W → y negative,
      // "climb"), but a tank driver expects W to advance — flip Y for the keyboard source only.
      const driveY = drive.source === 'keyboard' ? -drive.y : drive.y;
      const speedAlong = driveY * SPEED;
      const steerBite = THREE.MathUtils.clamp(speedAlong / SPEED, -1, 1); // -1..1 by motion
      this.hullYaw -= drive.x * DRIVE_STEER_RATE * steerBite * dt;
      this.group.rotation.set(0, this.hullYaw, 0);
      this.forward.set(0, 0, -1).applyQuaternion(this.group.quaternion);
      this.group.position.addScaledVector(this.forward, speedAlong * dt);

      // Turret aim. On a gamepad it's fully MANUAL — the right stick traverses and the turret HOLDS
      // when centred (you lay the gun yourself, helped by the targeting computer's lead marker). On
      // touch (no right stick) it auto-tracks the lead point so a one-thumb player can still fight.
      const turn = game.input.getRightStick();
      if (turn.source === 'gamepad') {
        this.turretYaw -= turn.x * PLAYER_TURRET_RATE * dt;
        this.turret.rotation.set(0, this.turretYaw, 0);
      } else if (aimWorld) {
        this.aimTurretToward(aimWorld, TURRET_SLEW * dt);
      }
    }
    // Sit on the rolling terrain, not a fixed plane, so the tank doesn't float.
    this.group.position.y = game.gameScene.heightAt(this.group.position.x, this.group.position.z);

    this.fireCooldown -= dt;
    // Manual fire when crewed (RT / touch FIRE); AI crews auto-fire when the lead point is in cone.
    const autoOnTarget = !!aimWorld && this.aimInCone(aimWorld);
    const firing = this.controlled ? game.fireHeld() : autoOnTarget;
    if (firing && this.fireCooldown <= 0) {
      // Crewed: the shell follows the BARREL (where you've aimed the turret); AI: the lead solution.
      const aim = this.controlled
        ? this.muzzleWorld().clone().add(this.turretForward().clone().multiplyScalar(FIRE_RANGE))
        : aimWorld!;
      this.shootToward(ctx, aim);
      // ACE BONUS: a player-crewed tank reloads faster (AI crews fire at baseline).
      this.fireCooldown = this.controlled ? FIRE_INTERVAL * 0.55 : FIRE_INTERVAL;
    }
    if (this.controlled) useStore.getState().setFiring(firing);

    // Targeting computer: publish the lock + lead solution for the HUD while the player is crewing it.
    if (this.controlled && !stank.ai && !stank.mapOpen) this.publishTargeting(target, aimWorld);
    else if (this.controlled) useStore.getState().setTankTargeting(null);
  }

  /** Project the locked target + its lead point to NDC for the HUD targeting overlay. */
  private publishTargeting(target: Entity | null, aimWorld: THREE.Vector3 | null) {
    const cam = this.camera;
    if (!cam || !target || !aimWorld) { useStore.getState().setTankTargeting(null); return; }
    cam.updateWorldMatrix(true, false);
    const proj = (p: THREE.Vector3) => {
      const v = p.clone().project(cam);
      const visible = v.z >= -1 && v.z <= 1 && Math.abs(v.x) <= 1.1 && Math.abs(v.y) <= 1.1;
      return { x: v.x, y: v.y, visible };
    };
    const lock = proj(target.position);
    const lead = proj(aimWorld);
    useStore.getState().setTankTargeting({
      lockNdcX: lock.x, lockNdcY: lock.y, lockVisible: lock.visible,
      leadNdcX: lead.x, leadNdcY: lead.y, leadVisible: lead.visible,
      range: this.muzzleWorld().distanceTo(target.position),
      onSolution: this.aimInCone(aimWorld),
    });
  }

  private acquireTarget(game: EntityCtx['game']): Entity | null {
    const muzzle = this.muzzleWorld();
    let best: Entity | null = null;
    let bestD = FIRE_RANGE * FIRE_RANGE;
    for (const e of game.findEnemies()) {
      if (!e.alive() || e.kind !== 'tank') continue; // armour fights armour (+ ground bosses)
      if (e.trainingTarget) continue;
      const d = e.position.distanceToSquared(muzzle);
      if (d < bestD) { bestD = d; best = e; }
    }
    return best;
  }

  /** Nearest enemy ground unit (tank / ground boss) within `maxRange` — the hunt target the tank
   *  drives toward in its default posture, regardless of whether it's yet in firing range. */
  private nearestEnemyArmor(game: EntityCtx['game'], maxRange: number): Entity | null {
    let best: Entity | null = null;
    let bestD = maxRange * maxRange;
    for (const e of game.findEnemies()) {
      if (!e.alive() || e.kind !== 'tank') continue;
      if (e.trainingTarget) continue;
      const d = e.position.distanceToSquared(this.group.position);
      if (d < bestD) { bestD = d; best = e; }
    }
    return best;
  }

  /** Nearest still-standing village — the fallback position to hold when no enemy armour is about. */
  private nearestVillage(game: EntityCtx['game']): THREE.Vector3 | null {
    let best: THREE.Vector3 | null = null;
    let bestD = Infinity;
    for (const v of game.gameScene.villages) {
      if (v.destroyed) continue;
      const d = v.position.distanceToSquared(this.group.position);
      if (d < bestD) { bestD = d; best = v.position; }
    }
    return best;
  }

  private muzzleWorld(): THREE.Vector3 {
    this.cannon.updateWorldMatrix(true, false);
    // getWorldPosition gives the barrel's CENTRE; the 4.5-long barrel points along turret
    // -Z, so the muzzle tip is ~2.25u forward of centre. Shells must spawn at the tip.
    this.cannon.getWorldPosition(this._muzzle);
    return this._muzzle.addScaledVector(this.turretForward(), BARREL_HALF);
  }

  private turretForward(): THREE.Vector3 {
    return this._fwd.set(0, 0, -1).applyQuaternion(this.turret.getWorldQuaternion(_tmpQ)).normalize();
  }

  private aimTurretToward(worldAim: THREE.Vector3, maxStep: number) {
    this.group.updateWorldMatrix(true, false);
    this._invMat.copy(this.group.matrixWorld).invert();
    this._toLocal.copy(worldAim).applyMatrix4(this._invMat);
    const desired = Math.atan2(-this._toLocal.x, -this._toLocal.z);
    let d = desired - this.turretYaw;
    d = Math.atan2(Math.sin(d), Math.cos(d));
    this.turretYaw += THREE.MathUtils.clamp(d, -maxStep, maxStep);
    this.turret.rotation.set(0, this.turretYaw, 0);
  }

  private aimInCone(aimWorld: THREE.Vector3): boolean {
    const muzzle = this.muzzleWorld();
    const to = aimWorld.clone().sub(muzzle);
    const d = to.length();
    if (d > FIRE_RANGE || d < 1e-3) return false;
    // YAW-only cone: the turret can't pitch, but shootToward lofts shells in 3D — so measure
    // traverse error in the ground plane. (The full-3D angle made every elevated boss part —
    // Tesla coil, breached worm coils — unhittable: the elevation alone exceeded the 7° cone.)
    const fwd = this.turretForward();
    to.y = 0;
    const flat = to.lengthSq() < 1e-6 ? 1 : to.normalize().dot(_flatFwd.set(fwd.x, 0, fwd.z).normalize());
    return flat > Math.cos(FIRE_CONE);
  }

  private shootToward(ctx: EntityCtx, aimWorld: THREE.Vector3) {
    const muzzle = this.muzzleWorld().clone();
    const dir = aimWorld.clone().sub(muzzle).normalize();
    const shell = new Shell(muzzle.clone(), dir.multiplyScalar(SHELL_SPEED), this.id, 'player');
    if (this.controlled) shell.damageAmount = 6; // ACE BONUS: harder-hitting shell when crewed
    ctx.game.add(shell);
    // Muzzle blast: bright flash, smoke ring, recoil-grade report.
    new Explosion(muzzle.clone(), 12, 0.2, 'fire').addTo(ctx);
    new Explosion(muzzle.clone(), 7, 0.7, 'smoke').setDrift(dir.clone().multiplyScalar(6)).addTo(ctx);
    ctx.game.audio.playBoom(0.55);
  }
}

const _tmpQ = new THREE.Quaternion();
const _flatFwd = new THREE.Vector3();


/**
 * Renault FT-17 light tank — the iconic Allied tank, deliberately distinct from the enemy
 * Mark IV rhomboids: compact riveted hull, the FT's signature high front idler "horns",
 * a long rear tail-skid (bêche), and a faceted domed turret that fully rotates. Allied
 * khaki livery with French tricolour roundels. Returns userData.turret (yaw pivot) and
 * userData.cannon (37 mm gun). Built at the same polygon budget as the rest of the game.
 */
export function makeAlliedTankMesh(): THREE.Group {
  const g = new THREE.Group();
  const hullMat = new THREE.MeshStandardMaterial({ color: 0x8a7d54, roughness: 0.9, flatShading: true });
  const hullDark = new THREE.MeshStandardMaterial({ color: 0x6a5f3e, roughness: 0.92, flatShading: true });
  const turretMat = new THREE.MeshStandardMaterial({ color: 0x847947, roughness: 0.85, flatShading: true });
  const tread = new THREE.MeshStandardMaterial({ color: 0x1a1814, roughness: 0.95 });
  const steel = new THREE.MeshStandardMaterial({ color: 0x2a2a2c, roughness: 0.4, metalness: 0.7 });
  const wood = new THREE.MeshStandardMaterial({ color: 0x4a3a2a, roughness: 0.95 });

  // Rivet helper — scatters small hemispheres so the armour reads as riveted plate.
  const rivetGeom = new THREE.SphereGeometry(0.05, 12, 8);
  const addRivets = (parent: THREE.Object3D, xs: number[], y: number, z: number) => {
    for (const x of xs) {
      const r = new THREE.Mesh(rivetGeom, steel);
      r.position.set(x, y, z);
      parent.add(r);
    }
  };

  // --- Central hull: a narrow riveted body that sits between the two track frames. ---
  const hullLow = new THREE.Mesh(new THREE.BoxGeometry(1.9, 1.3, 4.4, 6, 6, 16), hullMat);
  hullLow.position.set(0, 1.5, 0.1);
  g.add(hullLow);
  // Sloped front glacis.
  const glacis = new THREE.Mesh(new THREE.BoxGeometry(1.9, 1.3, 0.8, 6, 6, 4), hullDark);
  glacis.rotation.x = -0.5;
  glacis.position.set(0, 1.55, -2.1);
  g.add(glacis);
  // Engine deck (raised rear).
  const deck = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.7, 1.8, 6, 4, 8), hullDark);
  deck.position.set(0, 2.25, 1.35);
  g.add(deck);
  // Louvred rear plate.
  for (let i = 0; i < 5; i++) {
    const louvre = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.08, 0.18, 6, 1, 1), steel);
    louvre.position.set(0, 1.7 + i * 0.22, 2.25);
    g.add(louvre);
  }
  addRivets(g, [-0.85, -0.3, 0.3, 0.85], 2.0, -1.7);
  addRivets(g, [-0.85, 0.85], 1.1, 0.1);

  // --- Track frames: the FT's tall front-idler horns + long tail skid. ---
  for (const sx of [-1.35, 1.35]) {
    const frame = new THREE.Group();
    frame.position.x = sx;
    g.add(frame);

    // Main lower run (long box hugging the ground).
    const lower = new THREE.Mesh(new THREE.BoxGeometry(0.8, 1.0, 4.6, 3, 6, 18), tread);
    lower.position.set(0, 0.7, 0.0);
    frame.add(lower);
    // Sloped top run climbing toward the high front idler — the signature horn.
    const top = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.55, 3.6, 3, 4, 14), tread);
    top.position.set(0, 2.0, -0.6);
    top.rotation.x = -0.42;
    frame.add(top);

    // Large front idler wheel (set high — the horn apex).
    const idler = new THREE.Mesh(new THREE.CylinderGeometry(1.15, 1.15, 0.86, 56, 3), tread);
    idler.rotation.z = Math.PI / 2;
    idler.position.set(0, 2.55, -2.35);
    frame.add(idler);
    const idlerHub = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.34, 0.92, 32, 2), steel);
    idlerHub.rotation.z = Math.PI / 2;
    idlerHub.position.copy(idler.position);
    frame.add(idlerHub);
    // Idler spokes.
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const spoke = new THREE.Mesh(new THREE.BoxGeometry(0.85, 0.12, 0.5, 2, 1, 2), steel);
      spoke.position.set(0, 2.55 + Math.sin(a) * 0.6, -2.35 + Math.cos(a) * 0.6);
      spoke.rotation.x = a;
      frame.add(spoke);
    }

    // Rear drive sprocket (lower, smaller).
    const sprocket = new THREE.Mesh(new THREE.CylinderGeometry(0.78, 0.78, 0.84, 48, 2), tread);
    sprocket.rotation.z = Math.PI / 2;
    sprocket.position.set(0, 0.78, 2.3);
    frame.add(sprocket);
    // Sprocket teeth.
    for (let i = 0; i < 14; i++) {
      const a = (i / 14) * Math.PI * 2;
      const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.86, 0.16, 0.16, 2, 1, 1), steel);
      tooth.position.set(0, 0.78 + Math.sin(a) * 0.82, 2.3 + Math.cos(a) * 0.82);
      tooth.rotation.x = a;
      frame.add(tooth);
    }

    // Road wheels along the bottom run.
    for (let i = 0; i < 6; i++) {
      const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.42, 0.78, 32, 2), steel);
      wheel.rotation.z = Math.PI / 2;
      wheel.position.set(0, 0.42, -1.7 + i * 0.72);
      frame.add(wheel);
    }

    // Tail skid (bêche) — twin beams sweeping down and back to a point.
    const skid = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.4, 2.2, 2, 2, 8), wood);
    skid.position.set(0, 0.9, 3.4);
    skid.rotation.x = 0.55;
    frame.add(skid);
    const skidTip = new THREE.Mesh(new THREE.ConeGeometry(0.28, 0.7, 16, 2), wood);
    skidTip.position.set(0, 0.2, 4.3);
    skidTip.rotation.x = Math.PI / 2 + 0.3;
    frame.add(skidTip);

    // Tricolour roundel on the outer track face (Allied identity at a glance).
    const roundelFace = sx < 0 ? -0.41 : 0.41;
    for (const [r, col] of [[0.42, 0x1d3f8f], [0.28, 0xe8e8e8], [0.14, 0xc0202a]] as [number, number][]) {
      const ring = new THREE.Mesh(new THREE.CircleGeometry(r, 32),
        new THREE.MeshBasicMaterial({ color: col, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 }));
      ring.position.set(roundelFace, 1.0, 0.6);
      ring.rotation.y = sx < 0 ? -Math.PI / 2 : Math.PI / 2;
      frame.add(ring);
    }
  }

  // --- Rotating turret: faceted dome (Berliet "omnibus" cast turret) + cupola + gun. ---
  const turret = new THREE.Group();
  turret.position.set(0, 2.75, -0.3);
  g.add(turret);
  // Faceted drum (octagon-ish but high-seg for a smooth cast look).
  const drum = new THREE.Mesh(new THREE.CylinderGeometry(0.92, 1.02, 1.0, 40, 3), turretMat);
  drum.position.y = 0.5;
  turret.add(drum);
  // Domed top (top half of a sphere).
  const dome = new THREE.Mesh(new THREE.SphereGeometry(0.92, 48, 24, 0, Math.PI * 2, 0, Math.PI / 2), turretMat);
  dome.position.y = 1.0;
  turret.add(dome);
  // Mushroom cupola on top.
  const cupStalk = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 0.22, 24, 1), turretMat);
  cupStalk.position.y = 1.62;
  turret.add(cupStalk);
  const cupCap = new THREE.Mesh(new THREE.SphereGeometry(0.26, 32, 16, 0, Math.PI * 2, 0, Math.PI / 2), steel);
  cupCap.position.y = 1.74;
  turret.add(cupCap);
  // Vision slits around the dome.
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const slit = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.08, 0.04, 2, 1, 1), steel);
    slit.position.set(Math.sin(a) * 0.9, 0.8, Math.cos(a) * 0.9);
    slit.rotation.y = a;
    turret.add(slit);
  }
  addRivets(turret, [-0.3, 0, 0.3], 0.2, -0.86);

  // Mantlet + 37 mm gun (points along turret -Z). Cannon length 2*BARREL_HALF.
  const mantlet = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.5, 0.4, 4, 4, 3), hullDark);
  mantlet.position.set(0, 0.45, -0.85);
  turret.add(mantlet);
  const cannon = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.16, BARREL_HALF * 2, 40, 3), steel);
  cannon.rotation.x = Math.PI / 2;
  cannon.position.set(0, 0.45, -1.7);
  turret.add(cannon);
  // Muzzle collar at the barrel tip (cannon centre −1.7, half-length BARREL_HALF → tip at −3.7).
  const collar = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.18, 0.18, 28, 1), steel);
  collar.rotation.x = Math.PI / 2;
  collar.position.set(0, 0.45, -3.6);
  turret.add(collar);

  g.userData.turret = turret;
  g.userData.cannon = cannon;
  g.traverse((o) => { (o as THREE.Mesh).castShadow = false; (o as THREE.Mesh).receiveShadow = false; });
  return g;
}

/** Commander-hatch framing for the crewed view (turret-local; camera at ~(0, 2.5, 0.5) looking
 *  forward, pitched slightly down). Two parts:
 *   1. The open top hatch you're standing in — a coaming ring + flipped-back lid around the lower
 *      view, so you read as half out of the turret ("unbuttoned").
 *   2. A 37 mm gun at the COMMANDER'S SIGHTLINE — mantlet on the turret front shoulder and a long
 *      barrel running forward to just under the aim point. The real hull gun sits low under the tall
 *      domed turret and is hidden from the hatch, so the crewed view carries this one at a height the
 *      commander can actually see down. Shown only while crewed (hidden on the external model). */
export function makeTankInterior(): THREE.Group {
  const g = new THREE.Group();
  const turretMat = new THREE.MeshStandardMaterial({ color: 0x847947, roughness: 0.85, flatShading: true });
  const turretDk = new THREE.MeshStandardMaterial({ color: 0x6b6235, roughness: 0.85, flatShading: true });
  const rivetMat = new THREE.MeshStandardMaterial({ color: 0x5c5a50, roughness: 0.5, metalness: 0.5 });
  const gunMetal = new THREE.MeshStandardMaterial({ color: 0x42444b, roughness: 0.5, metalness: 0.6 });
  const gunBlue = new THREE.MeshStandardMaterial({ color: 0x2e3036, roughness: 0.45, metalness: 0.65 });

  // --- The turret you're looking out over ---------------------------------------------------------
  // A COMPLETE cast turret front (not a stub box): a domed front shell + a drum collar that runs off
  // the bottom of the frame, so it reads as the solid front of your own turret. Sits just ahead/below
  // the commander eye (≈ 2.45, 0.1). Matches the external turret's cast-khaki look.
  const dome = new THREE.Mesh(
    new THREE.SphereGeometry(1.18, 40, 24, 0, Math.PI * 2, 0, Math.PI * 0.6), turretMat);
  dome.position.set(0, 0.95, -0.15);
  g.add(dome);
  // Drum collar below the dome, flaring out and running off-frame (no abrupt bottom edge).
  const collar = new THREE.Mesh(new THREE.CylinderGeometry(1.18, 1.45, 1.6, 40, 2, true), turretMat);
  collar.position.set(0, 0.1, -0.15);
  g.add(collar);
  // Riveted seam band around the dome shoulder for read + scale.
  for (let i = 0; i < 14; i++) {
    const a = -Math.PI / 2 + (i / 13) * Math.PI; // front half only (what's in view)
    const r = new THREE.Mesh(new THREE.SphereGeometry(0.05, 8, 6), rivetMat);
    r.position.set(Math.cos(a) * 1.12, 1.05, -0.15 + Math.sin(a) * 1.12);
    g.add(r);
  }
  // Open hatch coaming + flipped-back lid just behind the eye, so you read as half out of the hatch.
  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.44, 0.08, 12, 28), turretDk);
  ring.rotation.x = Math.PI / 2; ring.position.set(0, 2.0, 0.55);
  g.add(ring);
  const lid = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.42, 0.06, 24), turretDk);
  lid.position.set(-0.6, 2.2, 0.95); lid.rotation.set(0.5, 0, 0.8);
  g.add(lid);

  // --- Sightline gun: rounded mantlet on the turret front + long barrel to the aim point ----------
  // (The real hull gun sits low under the dome and is hidden from the hatch, so the crewed view
  //  carries the gun at the commander's sightline.)
  const mantlet = new THREE.Mesh(new THREE.SphereGeometry(0.42, 24, 16), gunBlue);
  mantlet.scale.set(1.05, 1.05, 0.8);
  mantlet.position.set(0, 1.4, -1.0);
  g.add(mantlet);
  const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.17, 4.4, 32, 3), gunMetal);
  barrel.position.set(0, 1.34, -3.2);
  barrel.rotation.x = Math.PI / 2 + 0.025; // muzzle dips a hair, sits just under the crosshair
  g.add(barrel);
  const band = new THREE.Mesh(new THREE.CylinderGeometry(0.19, 0.19, 0.16, 28, 1), gunBlue);
  band.rotation.x = Math.PI / 2; band.position.set(0, 1.36, -2.1);
  g.add(band);
  const muzzle = new THREE.Mesh(new THREE.CylinderGeometry(0.19, 0.19, 0.3, 28, 1), gunBlue);
  muzzle.rotation.x = Math.PI / 2 + 0.025; muzzle.position.set(0, 1.24, -5.35);
  g.add(muzzle);

  g.traverse((o) => { (o as THREE.Mesh).castShadow = false; (o as THREE.Mesh).receiveShadow = false; });
  return g;
}

